'use client';

import { c as _c } from "react/compiler-runtime";
import './header.scss';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { usePathname } from 'next/navigation';
import { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce, throttle } from 'throttle-debounce';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { NavLink } from '@sola/ark-core/atoms/link/nav-link.tsx';
import { LogoChooser } from '@sola/ark-core/atoms/logos/logos.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { LogoName } from '@sola/schema/lib/logos.ts';
import type { Link as LinkProps } from '@sola/schema/lib/schema.ts';
import type { HeaderProps } from '@sola/schema/lib/skippy.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { useNavigationHover } from '../../hooks/use-navigation-hover.ts';
import { NavAccount } from '../../molecules/nav-account/nav-account.tsx';
import { NavBag } from '../../molecules/nav-bag/nav-bag.tsx';
import { NavSearch } from '../../molecules/nav-search/nav-search.tsx';
import { NavSheet } from '../../molecules/nav-sheet/nav-sheet.tsx';
import type { NavLevel } from '../../molecules/nav-stack/nav-stack.tsx';
import { NavStack } from '../../molecules/nav-stack/nav-stack.tsx';
import { getMenuItemColumns } from '../../utils/nav-utils.ts';

/**
 * Watches for pathname changes and calls onNavigate.
 * Wrapped in Suspense so usePathname() doesn't block the static shell.
 */
const PathnameWatcher = t0 => {
  const $ = _c(5);
  const {
    onNavigate
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== onNavigate) {
    t1 = () => {
      onNavigate();
    };
    $[0] = onNavigate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onNavigate || $[3] !== pathname) {
    t2 = [pathname, onNavigate];
    $[2] = onNavigate;
    $[3] = pathname;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useLayoutEffect(t1, t2);
  return null;
};
type CustomizeProps = {
  logo?: LogoName;
  link?: LinkProps;
  showShoppingBag?: boolean;
  showLogin?: boolean;
  showSearch?: boolean;
  experiments?: string[];
  urlLogin?: string;
  urlAccountHome?: string;
  userMenu?: React.ReactNode;
};
export const bodyScrollLock = (isLocked: boolean, lastScrollPosition: number) => {
  if (!document) {
    return;
  }
  const {
    body
  } = document;
  if (isLocked) {
    body.style.position = 'fixed';
    body.style.top = `-${lastScrollPosition}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return;
  }
  body.style.position = '';
  body.style.top = '';
  body.style.width = '';
  body.style.overflow = '';
  window.scrollTo(0, lastScrollPosition);
};
export const Header: React.FC<HeaderProps & CustomizeProps> = ({
  primaryMenu,
  logo = 'compassion',
  link,
  showShoppingBag,
  showLogin,
  showSearch,
  experiments,
  urlLogin,
  urlAccountHome,
  userMenu
}) => {
  const menuItems = primaryMenu.items || [];
  const {
    t
  } = useTranslation(['priority']);
  const [menuOpen, setMenuOpen] = useState('');
  const [menuClosing, setMenuClosing] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [navStack, setNavStack] = useState<NavLevel[]>([{
    items: menuItems
  }]);
  const isMobileMenuOpen = menuOpen === 'mobile';
  const isAccountMenuOpen = menuOpen === 'account';
  const isFullscreenMenuOpen = isMobileMenuOpen || isAccountMenuOpen;
  const closeMenuWithAnimation = () => {
    setMenuOpen('');
    setMenuClosing(true);
    setTimeout(() => setMenuClosing(false), 300);
  };
  const [headerHidden, setHeaderHidden] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTopRef = useRef(0);
  const mobileCloseButtonRef = useRef<HTMLButtonElement>(null);
  const mobileOpenButtonRef = useRef<HTMLButtonElement>(null);
  const accountTriggerRef = useRef<HTMLButtonElement>(null);
  const hamburgerTriggerRef = useRef<HTMLButtonElement>(null);
  const accountOpenedFromHamburgerRef = useRef(false);
  const previousMenuRef = useRef('');
  const {
    activeItem,
    setActiveItem,
    handleTriggerEnter,
    handleTriggerLeave,
    handleContentEnter,
    handleContentLeave,
    handleViewportEnter,
    handleViewportLeave,
    resetNavigation
  } = useNavigationHover({
    switchDelay: 200,
    exitDelay: 400
  });
  const handleNavigationReset = useCallback(() => {
    setIsNavigating(true);
    setMenuClosing(false);
    setMenuOpen('');
    setNavStack([{
      items: menuItems
    }]);
    resetNavigation();
    // Allow the instant close to render, then clear the flag
    requestAnimationFrame(() => setIsNavigating(false));
  }, [menuItems, resetNavigation]);
  const throttledHandleScroll = useMemo(() => throttle(250, () => {
    if (menuOpen === 'mobile' || menuOpen === 'account' || activeItem) {
      return;
    }
    resetNavigation();
    const reappearTimeout = 5000; // 5 seconds
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // check > 80 to prevent whitespace behind the navbar appearing and mitigate rubberbanding on iOS
    if (scrollTop > lastScrollTopRef.current && scrollTop > 80) {
      setHeaderHidden(true);
    } else {
      setHeaderHidden(false);
    }
    lastScrollTopRef.current = scrollTop;
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      setHeaderHidden(false);
    }, reappearTimeout);
  }), [menuOpen, activeItem, resetNavigation]);

  // Mobile menu open/close + scroll lock
  useEffect(() => {
    if (isMobileMenuOpen) {
      previousMenuRef.current = 'mobile';
      const debouncedFocus = debounce(100, () => {
        mobileCloseButtonRef.current?.focus();
      });
      debouncedFocus();
      bodyScrollLock(true, lastScrollTopRef.current);
      return;
    }
    if (previousMenuRef.current === 'mobile') {
      const debouncedFocus_0 = debounce(200, () => {
        hamburgerTriggerRef.current?.focus();
        previousMenuRef.current = '';
      });
      debouncedFocus_0();
      bodyScrollLock(false, lastScrollTopRef.current);
    }
  }, [isMobileMenuOpen]);

  // Account menu open/close + scroll lock
  useEffect(() => {
    if (isAccountMenuOpen) {
      previousMenuRef.current = 'account';
      bodyScrollLock(true, lastScrollTopRef.current);
      return;
    }
    if (previousMenuRef.current === 'account') {
      const debouncedFocus_1 = debounce(200, () => {
        if (accountOpenedFromHamburgerRef.current) {
          accountOpenedFromHamburgerRef.current = false;
          hamburgerTriggerRef.current?.focus();
        } else {
          accountTriggerRef.current?.focus();
        }
        previousMenuRef.current = '';
      });
      debouncedFocus_1();
      bodyScrollLock(false, lastScrollTopRef.current);
    }
  }, [isAccountMenuOpen]);
  useEffect(() => {
    window.addEventListener('scroll', throttledHandleScroll);
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current); // Cleanup timeout on unmount
      }
    };
  }, [throttledHandleScroll]);
  useEffect(() => {
    if (menuOpen === 'mobile' || menuOpen === 'account') {
      return;
    }
    if (activeItem) {
      setMenuOpen(activeItem);
      return;
    }
    setMenuOpen('');
  }, [activeItem, menuOpen]);
  return <header role="banner" className={cn('Header', {
    'Header--hidden': headerHidden
  })}>
      <Suspense>
        <PathnameWatcher onNavigate={handleNavigationReset} />
      </Suspense>
      <ButtonStyle>
        <a className="Header__skip-link" href="#main">
          {t('header.skipLink')}
        </a>
      </ButtonStyle>
      <div className={cn('Header__container', {
      'is-mobile-open': isFullscreenMenuOpen,
      'is-menu-closing': menuClosing,
      'is-navigating': isNavigating
    })}>
        <div className="Header__menu-list-container w-full">
          <div className="Header__logo-container">
            <Link href={link?.link || '/'} title={link?.title || t('header.logoAlt')}>
              <LogoChooser name={logo} className="Header__logo" color="full" aria-labelledby="Header-logo-label" priority />
              <span id="Header-logo-label" className="sr-only">
                {t('header.logoAlt', {
                title: link?.title
              })}
              </span>
            </Link>
          </div>

          <NavigationMenu.Root aria-label="Main navigation" value={menuOpen} className="Header__root flex-col hidden lg:flex">
            <NavigationMenu.List className="Header__menu-list" asChild>
              <div>
                {/* desktop menu */}
                <ul className="Header__desktop-items xl:mx-roomy-xs">
                  {menuItems.map(item => {
                  const hasChildren = Boolean(item.items?.length);
                  const columns = getMenuItemColumns(item);
                  return <NavigationMenu.Item value={item.title} key={item.title} className="flex justify-center">
                        <NavigationMenu.Trigger onTouchEnd={e => e.preventDefault()} onPointerEnter={() => item.title && handleTriggerEnter(item.title)} onPointerLeave={handleTriggerLeave} onFocus={() => {
                      if (menuOpen && item.title && hasChildren) {
                        handleTriggerEnter(item.title);
                      }
                    }} onBlur={() => {
                      if (menuOpen && hasChildren) {
                        handleTriggerLeave();
                      }
                    }} onClick={e_0 => {
                      if (!item.title) {
                        return;
                      }
                      trackClick(item.title, item?.link || '#', 'Header__trigger');
                      if (!hasChildren) {
                        return;
                      }
                      if (menuOpen && e_0.detail !== 0) {
                        e_0.preventDefault();
                      }
                      // actions for screenreaders (tabs)
                      if (menuOpen === item.title) {
                        setActiveItem('');
                      } else {
                        setActiveItem(item.title);
                      }
                    }} className={cn('Header__trigger flex items-center justify-between', {
                      'Header__trigger--active': menuOpen === item.title
                    })} asChild>
                          {hasChildren ? <button type="button">
                              <p className="label label-potent label-neutral flex items-center gap-snug-md">
                                {item.title}
                                <ArkIcon iconName="chevron-down" className="Header__chevron" />
                              </p>
                            </button> : <NavLink href={item?.link || '#'} title={item?.title} target={item?.target} className="Header__desktop-nav-item label label-potent label-neutral">
                              {item.title}
                            </NavLink>}
                        </NavigationMenu.Trigger>
                        {hasChildren && <NavigationMenu.Content className="Header__content Header__container flex justify-center" onPointerEnter={handleContentEnter} onPointerLeave={handleContentLeave} onFocus={handleContentEnter} onBlur={handleContentLeave}>
                            <div className="w-full max-w-xl flex flex-col">
                              <div className="pt-roomy-xs pb-comfy-lg">
                                <div className="Header__link-container pb-snug-md">
                                  <LinkStyle noPadding noUnderline size="lg" iconAfter="arrow-right">
                                    <Link href={item?.link || '#'} target={item?.target} title={item?.title}>
                                      {item.title}
                                    </Link>
                                  </LinkStyle>
                                </div>
                              </div>
                              <div className="Header__navsheet-container gap-comfy-xl pb-comfy-xl pt-comfy-xs">
                                {columns.map((col, idx) => {
                            return <NavSheet key={col.map(ci => ci.title).join(',')} items={col} {...idx === 2 ? {
                              cta: item.cta
                            } : {}} />;
                          })}
                              </div>
                            </div>
                          </NavigationMenu.Content>}
                      </NavigationMenu.Item>;
                })}
                </ul>
              </div>
            </NavigationMenu.List>

            {/* The entering animation seems to like having this here. Not sure why. */}
            <NavigationMenu.NavigationMenuIndicator />

            <div className="Header__viewport-position">
              <NavigationMenu.Viewport className="Header__menu-viewport flex justify-center" onPointerEnter={handleViewportEnter} onPointerLeave={handleViewportLeave} onFocus={handleViewportEnter} onBlur={handleViewportLeave} />
            </div>
          </NavigationMenu.Root>

          <NavigationMenu.Root onValueChange={value => {
          const wasFullscreen = menuOpen === 'mobile' || menuOpen === 'account';
          setMenuOpen(value);
          if (value === 'mobile' || value === 'account' || wasFullscreen) {
            resetNavigation();
          }
        }} value={menuOpen} className="Header__root flex flex-col" aria-label="Action navigation">
            <NavigationMenu.List className="Header__menu-list" asChild>
              <div className="Header__desktop-actions flex items-center gap-comfy-sm justify-end">
                <div className="flex flex-row gap-snug-md md:gap-snug-xl lg:gap-comfy-sm">
                  <div className="flex gap-snug-md md:gap-snug-xl">
                    {showSearch && <NavSearch />}
                    {showShoppingBag && <NavBag />}
                  </div>
                  {showLogin && <NavAccount urlLogin={urlLogin} urlAccountHome={urlAccountHome} userMenu={userMenu} triggerRef={accountTriggerRef} onClose={() => {
                  closeMenuWithAnimation();
                  resetNavigation();
                }} />}

                  {/* mobile menu */}
                  <NavigationMenu.Item className="Header__nav-action Header__mobile-menu-trigger flex lg:hidden items-center" key="mobile hamburger menu" value="mobile" asChild>
                    <div>
                      <NavigationMenu.Trigger ref={hamburgerTriggerRef} onPointerEnter={e_1 => e_1.stopPropagation()} onPointerMove={e_2 => e_2.preventDefault()} onPointerLeave={e_3 => e_3.preventDefault()} onClick={() => {
                      trackClick(t('header.menu'));
                    }} className="flex items-center p-comfy-xs rounded-full" title={t('header.menu')}>
                        <ArkIcon iconName="menu-01" className="Header__nav-icon" />
                      </NavigationMenu.Trigger>
                      <NavigationMenu.Content className="Header__content Header__content--mobile flex flex-col" onPointerLeave={e_4 => e_4.preventDefault()}>
                        <div className={cn('Header__mobile-nav flex px-comfy-xs py-comfy-lg items-center', {
                        'justify-end': navStack.length === 1,
                        'justify-between': navStack.length > 1
                      })}>
                          {Boolean(navStack.length > 1) && <button ref={mobileOpenButtonRef} className="flex items-center" type="button" onClick={() => {
                          setNavStack(prev => prev.slice(0, -1));
                        }}>
                              <ArkIcon iconName="arrow-left" className="Header__nav-icon" />
                            </button>}

                          <span className="flex items-center gap-snug-md md:gap-snug-xl lg:gap-comfy-sm">
                            {showSearch && <NavSearch />}
                            {showShoppingBag && <NavBag />}
                            {showLogin && <NavAccount urlLogin={urlLogin} urlAccountHome={urlAccountHome} userMenu={userMenu} asTriggerOnly onOpen={() => {
                            accountOpenedFromHamburgerRef.current = true;
                            setMenuOpen('account');
                          }} />}
                            <button ref={mobileCloseButtonRef} className="Header__nav-action flex items-center p-comfy-xs" type="button" title={t('header.menu')} onClick={() => {
                            closeMenuWithAnimation();
                            setNavStack([{
                              items: menuItems
                            }]);
                            resetNavigation();
                          }}>
                              <ArkIcon iconName="x-close" className="Header__nav-icon" />
                            </button>
                          </span>
                        </div>
                        <NavStack stack={navStack} setStack={setNavStack} showMyAccountSection={showLogin} className="pb-roomy-sm" />
                      </NavigationMenu.Content>
                    </div>
                  </NavigationMenu.Item>
                </div>
              </div>
            </NavigationMenu.List>

            {/* The entering animation seems to like having this here. Not sure why. */}
            <NavigationMenu.NavigationMenuIndicator />

            <div className="Header__viewport-position">
              <NavigationMenu.Viewport className="Header__menu-viewport flex justify-center" onPointerEnter={handleViewportEnter} onPointerLeave={handleViewportLeave} />
            </div>
          </NavigationMenu.Root>
        </div>
      </div>
    </header>;
};
export default Header;